/**
 * Main-thread cache for the `x-team-id` header value.
 *
 * - `getActiveTeamIdForRequests()` — synchronous read. `null` if never set.
 * - `getActiveTeamIdReady()` — resolves once the value has been set at least
 *   once (even to `null`). The RPC link's `async headers()` awaits this so no
 *   request can go out with an *unset* cache.
 *
 * "unset" and "logged out (null)" are different states:
 *   - unset → RPC should wait (cache hasn't been seeded yet)
 *   - null  → RPC proceeds without `x-team-id` (anonymous / bootstrap)
 *
 * Seeded by `authLoader` right after `getSession()`, re-seeded on team switch
 * by `WorkspaceContext.setActiveTeam`.
 */

let cache: string | null = null;

let ready = createDeferred<string | null>();
let readyHasResolved = false;

export const setActiveTeamIdForRequests = (teamId: string | null): void => {
	cache = teamId;
	if (!readyHasResolved) {
		readyHasResolved = true;
		ready.resolve(teamId);
	} else {
		// Replace with an already-resolved promise so any awaiter created
		// *after* this point resolves synchronously with the latest value.
		ready = createResolved(teamId);
	}
};

export const getActiveTeamIdForRequests = (): string | null => cache;

/**
 * Awaited by the RPC link's `async headers()`. Blocks until the cache has
 * been seeded at least once; then resolves near-instantly on every call.
 */
export const getActiveTeamIdReady = (): Promise<string | null> => ready.promise;

/**
 * Per-call context the RPC link reads: `noTeam` says the procedure is not
 * team-scoped (a public read such as the speaker link's, or a session-only one),
 * so the call must not wait for a seed that may never come.
 */
export interface RpcCallContext {
	noTeam?: boolean;
}

/**
 * The team id a call should carry. A team-scoped call waits for the seed
 * (`authLoader` sets it before any such component mounts). A call that
 * needs no team goes out at once with whatever is cached: the seed only
 * happens once a session exists, and the calls that run before one — the
 * tenant branding behind the login page, the speaker link's page with no
 * session at all — would otherwise hang forever on a promise nothing
 * resolves, which is what "the login page keeps spinning" was.
 */
export const teamIdForCall = (context: RpcCallContext): Promise<string | null> =>
	context.noTeam ? Promise.resolve(cache) : ready.promise;

interface Deferred<T> {
	promise: Promise<T>;
	resolve: (v: T) => void;
}

function createDeferred<T>(): Deferred<T> {
	let resolve!: (v: T) => void;
	const promise = new Promise<T>((r) => {
		resolve = r;
	});
	return { promise, resolve };
}

function createResolved<T>(v: T): Deferred<T> {
	return { promise: Promise.resolve(v), resolve: () => {} };
}
